import _ from 'lodash';

/**
 * Convert the server's keyed collections (`{ 'id-…': { _meta: { index, … }, … } }`)
 * back into arrays ordered by `_meta.index`, recursively.
 *
 * Every entry is KEPT. The previous implementation wrote each entry into an
 * array slot by its index (`array[_meta.index] = entry`), so two entries sharing
 * an index silently collapsed into one and the loser vanished from the UI,
 * the export and every save — while still existing in the DB (#215: provider
 * 413 had 33 such hidden products). Duplicate indexes can exist because a new
 * product's index used to be derived client-side from the rows it could see.
 * Entries are now stable-sorted by index (ties keep the server's key order, i.e.
 * row-id order) and renumbered 0..n-1, so a collision only affects ordering.
 */
const objectsToArrays = function (objectToArray: any, isOriginInstance: boolean = true) {
  const toIndex = (value: any): number => {
    const index = Number(value);

    return Number.isFinite(index) ? index : Number.MAX_SAFE_INTEGER;
  };

  const reIndexArray = (arr: any[]) => {
    return arr.map((item, i) => {
      if (_.isPlainObject(item) && item._meta) {
        item._meta.index = i;
      }

      return item;
    });
  };

  let data = _.cloneDeep(objectToArray);

  if (_.isPlainObject(data)) {
    const entries: { position: number; index: number; value: any }[] = [];
    let position = 0;

    for (const key in data) {
      if (!data.hasOwnProperty(key)) {
        continue;
      }

      if (_.has(data[key], '_meta')) {
        // Keyed-collection entry: unwrap primitives, recurse into objects.
        const raw = data[key];
        const value = _.has(raw, '_meta.value') ? raw._meta.value : raw;

        entries.push({
          position: position++,
          index: toIndex(raw._meta.index),
          value: objectsToArrays(value, false),
        });

        delete data[key];
      } else {
        data[key] = objectsToArrays(data[key], false);
      }
    }

    if (entries.length) {
      entries.sort((a, b) => a.index - b.index || a.position - b.position);

      return reIndexArray(entries.map((entry) => entry.value));
    }

    return data;
  }

  if (_.isArray(data)) {
    // A real array (already converted upstream): drop holes, renumber.
    // cloneDeep turns holes into explicit `undefined` entries, so filter on
    // the value rather than relying on sparse-array iteration.
    return reIndexArray(data.filter((item: any) => item !== undefined));
  }

  return data;
};

export { objectsToArrays };
